import { useState } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { useIntl } from 'react-intl';
import {
  Calendar,
  type DayAvailability as CalendarDayAvailability,
} from '@understory-io/leaf/calendar';
import { AvailabilityUnavailableNotice } from '~/ui/availability-unavailable';

/** What the availability API answers per day. Days it doesn't mention have
 *  no events at all. */
type DayAvailability = 'available' | 'soldOut' | 'unknown';

type AvailabilityCalendarProps = {
  /** Currently selected date (YYYY-MM-DD) */
  selectedDate: string | null;
  /** Map of date string (YYYY-MM-DD) -> availability status */
  availability: Record<string, DayAvailability>;
  /** Set of month keys (YYYY-MM) whose data has been fetched */
  readyMonths?: ReadonlySet<string>;
  /** Called when user selects a date */
  onDateSelect: (date: string) => void;
  /** Called with the first of the month (YYYY-MM-01) when the visible month
   *  changes, for prefetching */
  onMonthChange?: (startOfMonth: string) => void;
  /** Date the calendar should initially focus on (YYYY-MM-DD). Defaults to the
   *  selected date, then today. Used to anchor the calendar on the first
   *  upcoming event so the user doesn't have to page forward to find it. */
  initialFocusDate?: string | null;
  /** Earliest selectable day (YYYY-MM-DD). Defaults to today. */
  min?: string;
  /** Month keys (YYYY-MM) whose availability could not be loaded. Their days
   *  can't be picked, and the month shows a notice offering `onRetry`. */
  failedMonths?: ReadonlySet<string>;
  onRetry?: () => void;
};

export type { AvailabilityCalendarProps, DayAvailability };

/**
 * Leaf's `Calendar` fed by the availability API: this component owns which
 * days are bookable, which month is loading, and the retry notice for a month
 * that failed to load. Leaf owns everything the visitor sees and operates.
 */
export function AvailabilityCalendar({
  selectedDate,
  availability,
  readyMonths,
  onDateSelect,
  onMonthChange,
  initialFocusDate,
  min,
  failedMonths,
  onRetry,
}: AvailabilityCalendarProps) {
  const intl = useIntl();

  const minDate = min ?? today(getLocalTimeZone()).toString();

  // Resolve the initial focused date, falling back through: explicit focus
  // target → selected date → minDate (today). Clamp to minDate so we never
  // open on a month of disabled past days. ISO dates compare as strings.
  const focusCandidate = initialFocusDate ?? selectedDate ?? minDate;
  const initialFocus = focusCandidate < minDate ? minDate : focusCandidate;
  const [visibleMonth, setVisibleMonth] = useState(initialFocus.slice(0, 7));

  const isFailed = failedMonths?.has(visibleMonth) ?? false;
  const isLoading =
    isFailed || (readyMonths ? !readyMonths.has(visibleMonth) : false);

  // Leaf only knows available and unavailable; a day the API doesn't answer
  // for is left out, which Leaf renders as unknown. Rebuilt every render, not
  // memoised: `useAvailability` fills its map in place, so the object's
  // identity doesn't change when a month arrives.
  const calendarAvailability: Record<string, CalendarDayAvailability> = {};
  for (const [date, status] of Object.entries(availability)) {
    if (status === 'available') calendarAvailability[date] = 'available';
    else if (status === 'soldOut') calendarAvailability[date] = 'unavailable';
  }

  return (
    <Calendar
      label={intl.formatMessage({
        defaultMessage: 'Select a date',
        description: 'Accessible label for the availability calendar',
      })}
      value={selectedDate ?? undefined}
      onChange={onDateSelect}
      min={minDate}
      defaultMonth={initialFocus.slice(0, 7)}
      availability={calendarAvailability}
      // A failed month reads as loading: its days can't be picked and carry no
      // availability, and the notice below says why.
      loading={isLoading}
      onVisibleMonthChange={(month) => {
        setVisibleMonth(month);
        onMonthChange?.(`${month}-01`);
      }}
      footer={
        isFailed && onRetry ? (
          <AvailabilityUnavailableNotice onRetry={onRetry} />
        ) : undefined
      }
    />
  );
}
